// Редовият модел и обобщението на справка „Фактури".
//
// Етикетите (вид, състояние) се разрешават ТУК, преди редовете да влязат в
// таблицата. TanStack кешира стойността на клетката за живота на `data`, а
// React Query пази СЪЩАТА референция при непроменен отговор — етикет, сглобен
// в `accessorFn`, се заковава на езика от първия рендер и смяната на езика
// никога не стига до клетката.
//
// Всички суми минават през `parseDecimal`: BE-то праща Decimal полетата като
// JSON низове, независимо че типът в `api/invoices.ts` ги описва като `number`.

import { parseDecimal } from "../../../../components/dataTable";
import type { Invoice } from "../../../../api/invoices";

export type InvoiceRow = {
  id: number;
  number: string;
  /** Суров ISO низ — по него филтрира диапазонът от дати и тече подредбата. */
  issueDate: string;
  /** Моментът на съставяне — само за колоната „Час" и за Excel. */
  createdAt: string;
  customerName: string;
  customerEik: string;
  /** Преведеният етикет на вида — чипът и чеклистът четат него. */
  typeLabel: string;
  isSimplified: boolean;
  /** Преведеният етикет на състоянието — „Издадена" / „Анулирана". */
  statusLabel: string;
  isCancelled: boolean;
  taxable: number;
  nonTaxable: number;
  vat: number;
  grandTotal: number;
  /** Ефективна ставка на фактурата — виж `effectiveVatRate`. */
  vatRate: number;
};

export type InvoicesSummary = {
  count: number;
  taxable: number;
  nonTaxable: number;
  vat: number;
  grandTotal: number;
  cancelledCount: number;
};

/** Етикетите идват отвън — модулът не пипа i18n и се тества без него. */
export type InvoiceLabels = {
  tax: string;
  simplified: string;
  issued: string;
  cancelled: string;
};

/**
 * Ефективната ДДС ставка на фактура: начисленият данък върху облагаемата
 * основа. Това е числото, което човек чете на хартиената фактура — затова се
 * извежда от сумите, а не от `lines[].vat_rate` (един документ може да носи
 * редове с различни ставки). Без облагаема част ставката е 0 %.
 */
export const effectiveVatRate = (taxable: number, vat: number): number => {
  if (taxable <= 0) return 0;
  return Math.round((vat / taxable) * 100);
};

export const buildInvoiceRows = (
  invoices: readonly Invoice[],
  labels: InvoiceLabels,
): InvoiceRow[] =>
  invoices.map((invoice) => {
    const taxable = parseDecimal(invoice.subtotal_taxable);
    const vat = parseDecimal(invoice.vat_total);
    const isSimplified = invoice.invoice_type === "simplified";
    const isCancelled = Boolean(invoice.cancelled_at);

    return {
      id: invoice.id,
      number: invoice.invoice_number,
      issueDate: invoice.issue_date,
      createdAt: invoice.created_at,
      customerName: invoice.customer_name,
      customerEik: invoice.customer_eik,
      typeLabel: isSimplified ? labels.simplified : labels.tax,
      isSimplified,
      statusLabel: isCancelled ? labels.cancelled : labels.issued,
      isCancelled,
      taxable,
      nonTaxable: parseDecimal(invoice.subtotal_non_taxable),
      vat,
      grandTotal: parseDecimal(invoice.grand_total),
      vatRate: effectiveVatRate(taxable, vat),
    };
  });

/**
 * Сборовете за кутийките.
 *
 * Анулираните фактури СЕ броят в сумите — така беше и досега и така се сверява
 * издаденото по номера с дневника. Затова кутийката „Анулирани" се показва при
 * всяка анулирана фактура: тя е обяснението защо сумите са по-високи от
 * реално дължимото.
 */
export const summarizeInvoices = (
  rows: readonly InvoiceRow[],
): InvoicesSummary =>
  rows.reduce<InvoicesSummary>(
    (accumulator, row) => ({
      count: accumulator.count + 1,
      taxable: accumulator.taxable + row.taxable,
      nonTaxable: accumulator.nonTaxable + row.nonTaxable,
      vat: accumulator.vat + row.vat,
      grandTotal: accumulator.grandTotal + row.grandTotal,
      cancelledCount: accumulator.cancelledCount + (row.isCancelled ? 1 : 0),
    }),
    {
      count: 0,
      taxable: 0,
      nonTaxable: 0,
      vat: 0,
      grandTotal: 0,
      cancelledCount: 0,
    },
  );
